import { useState, useRef, type SubmitEventHandler } from 'react';
import { useElements } from '@stripe/react-stripe-js';
import { FormattedMessage } from 'react-intl';

import { type StripeElementChangeEvent, type StripeElements } from '@stripe/stripe-js';
import { Button } from '../common/Button';
import SelectedPlanBlock, { type SelectedPlanBlockProps } from '../common/SelectedPlanBlock';
import NarrowColumnContainer from '../NarrowColumnContainer';
import Layout from '../Layout';
import {
  CardNumberField,
  CardExpirationField,
  CardCVCField,
  CountryField,
  StateField,
  PostalCodeField,
} from './CardInputFields';
import CardFormFooter from './CardFormFooter';

import styles from './CardFormScreen.module.scss';

import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { minPostalCodeValidLength } from '@/utils/consts/input';
import { type RequiredBillingInfo } from '@/utils/stripe/paymentApi';
import clsx from 'clsx';

interface CardFormValidationState {
  number: boolean;
  expiration: boolean;
  cvc: boolean;
  country: boolean;
  postalCode: boolean;
  state?: boolean;
}

interface CardFormScreenProps {
  selectedPlanBlockData: SelectedPlanBlockProps;
  isSubmitting: boolean;
  onBackButtonClick: () => void;
  onCardFormSubmit: (elements: StripeElements | null, billingInfo: RequiredBillingInfo) => Promise<void>;
  isWebRoute?: boolean;
}

const countriesWithStates = ['US'];

const CardFormScreen = ({
  selectedPlanBlockData,
  isSubmitting,
  onBackButtonClick,
  onCardFormSubmit,
  isWebRoute,
}: CardFormScreenProps) => {
  const elements = useElements();
  const countryFieldRef = useRef<HTMLSelectElement>(null);
  const stateFieldRef = useRef<HTMLSelectElement>(null);
  const postalCodeFieldRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<CardFormValidationState>({
    number: false,
    expiration: false,
    cvc: false,
    country: false,
    postalCode: false,
  });
  const focusFirstInvalidField = (state: CardFormValidationState) => {
    if (!state.number) {
      elements?.getElement('cardNumber')?.focus();
    } else if (!state.expiration) {
      elements?.getElement('cardExpiry')?.focus();
    } else if (!state.cvc) {
      elements?.getElement('cardCvc')?.focus();
    } else if (!state.country) {
      countryFieldRef?.current?.focus();
    } else if (!state.state) {
      stateFieldRef?.current?.focus();
    } else if (!state.postalCode) {
      postalCodeFieldRef?.current?.focus();
    }
  };
  /** On card form autofill, there is a concurrency issue, that may result in a complete field being focused,
   * that's why here it takes value directly from React state setter.
   */
  const updateStateAndFocusNextField = (fieldName: keyof CardFormValidationState, value: boolean): void => {
    setFormState((state) => {
      const newValue = { ...state, [fieldName]: value };

      // considering refocus, only if current input is complete
      if (value) {
        focusFirstInvalidField(newValue);
      }

      return newValue;
    });
  };

  const onCardChange = (event: StripeElementChangeEvent) => {
    updateStateAndFocusNextField('number', event.complete);
  };
  const onExpirationChange = (event: StripeElementChangeEvent) => {
    updateStateAndFocusNextField('expiration', event.complete);
  };
  const onCVCChange = (event: StripeElementChangeEvent) => {
    updateStateAndFocusNextField('cvc', event.complete);
  };

  const [countryInput, setCountryInput] = useState<string>('');
  const [needsState, setNeedsState] = useState<boolean>(false);
  const onCountryChange = (value: string) => {
    setNeedsState(Boolean(countriesWithStates.find((item) => item === value)));
    setCountryInput(value);
    updateStateAndFocusNextField('country', Boolean(value));
  };

  const [stateInput, setStateInput] = useState<string | undefined>();
  const onStateChange = (value: string) => {
    setStateInput(value);
    updateStateAndFocusNextField('state', Boolean(value));
  };

  const [postalCodeInput, setPostalCodeInput] = useState<string>('');
  const onPostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCodeInput(event.currentTarget.value);
    updateStateAndFocusNextField(
      'postalCode',
      event.target.checkValidity() && event.target.value.length >= minPostalCodeValidLength,
    );
  };

  const onSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    void onCardFormSubmit(elements, {
      country: countryInput,
      postalCode: postalCodeInput,
      ...(stateInput && { state: stateInput }),
    });
  };

  const [hasSubmittedCheckoutEvent, setHasSubmittedCheckoutEvent] = useState<boolean>(false);
  const onFocus = () => {
    if (!hasSubmittedCheckoutEvent) {
      setHasSubmittedCheckoutEvent(true);
      AnalyticsEventHandler.trackStartedWithCard();
    }
  };

  const isSubmitButtonDisabled =
    !formState.number ||
    !formState.expiration ||
    !formState.cvc ||
    !formState.country ||
    (needsState && !formState.state) ||
    !formState.postalCode ||
    isSubmitting;

  return (
    <Layout>
      <NarrowColumnContainer onBackButtonClick={onBackButtonClick}>
        <SelectedPlanBlock {...selectedPlanBlockData} />

        <form onSubmit={onSubmit}>
          <span className={styles.label}>
            <FormattedMessage
              id="cardFormScreen.cardInfoLabel"
              defaultMessage="Card information"
              description="Label for card info block of the form, i.e. card number, expiration and CVV"
            />
          </span>

          <div className={styles.formGrid}>
            <CardNumberField className={styles.cardNumberField} onChange={onCardChange} onFocus={onFocus} />
            <CardExpirationField onChange={onExpirationChange} onFocus={onFocus} />
            <CardCVCField onChange={onCVCChange} onFocus={onFocus} />
          </div>

          <span className={styles.label}>
            <FormattedMessage
              id="cardFormScreen.billingAddressLabel"
              defaultMessage="Billing address"
              description="Label for billing address block of the form, i.e. country and postal code"
            />
          </span>
          <div className={clsx(styles.billingInfoGrid, needsState && styles.billingInfoGridWithState)}>
            <CountryField ref={countryFieldRef} onChange={onCountryChange} />
            {needsState && (
              <div className={styles.stateField}>
                <StateField ref={stateFieldRef} onChange={onStateChange} />
              </div>
            )}
            <PostalCodeField ref={postalCodeFieldRef} onChange={onPostalCodeChange} />
          </div>

          <Button kind="primary" type="submit" disabled={isSubmitButtonDisabled} loading={isSubmitting}>
            <FormattedMessage
              id="cardFormScreen.continuePayment"
              defaultMessage="Continue"
              description="Submits card form for payment"
            />
          </Button>

          <Button
            kind="tertiary"
            className={styles.changeMethodButton}
            disabled={isSubmitting}
            onClick={onBackButtonClick}
          >
            <FormattedMessage
              id="cardFormScreen.changePaymentMethod"
              defaultMessage="Change Payment Method"
              description="Button, brings user from card form back to payment method selection"
            />
          </Button>
        </form>
      </NarrowColumnContainer>

      <CardFormFooter isWebRoute={isWebRoute} />
    </Layout>
  );
};

export default CardFormScreen;
